import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Text } from '@heartinternet/hi-ui';
import { Input } from '@heartinternet/hi-ui';
import { Button } from '@heartinternet/hi-ui';
import { Badge, BadgeVariant } from '@heartinternet/hi-ui';
import { EmptyState } from '@heartinternet/hi-ui';
import { Spinner } from '@heartinternet/hi-ui';
import { Flex } from '@heartinternet/hi-ui';
import { Table, Column, PaginationConfig } from '@heartinternet/hi-ui';
import { Icon } from '@heartinternet/hi-ui';
import './TicketListPage.css';

interface Ticket {
  id: number;
  service: string;
  title: string;
  status: string;
  updated: string;
  update_count: number;
}

interface JqGridResponse {
  page: number;
  total: number;
  records: number;
  rows: Ticket[];
}

const STATUS_DISPLAY: Record<string, { text: string; color: string; badge: BadgeVariant }> = {
  'AWAITING-RESPONSE': {
    text: 'Response required',
    color: 'var(--hi-color-error, #dc2626)',
    badge: 'error',
  },
  'AWAITING-SUPPORT': {
    text: 'Awaiting support',
    color: 'var(--hi-color-info, #2563eb)',
    badge: 'info',
  },
  'AWAITING-CUSTOMER': {
    text: 'Awaiting customer',
    color: 'var(--hi-color-warning, #d97706)',
    badge: 'warning',
  },
  OPEN: { text: 'Open', color: 'var(--hi-color-success, #16a34a)', badge: 'success' },
  CLOSED: { text: 'Closed', color: 'var(--hi-color-neutral, #6b7280)', badge: 'neutral' },
};

export interface TicketListPageProps {
  /** Endpoint to fetch tickets from */
  fetchUrl?: string;
  /** URL for the "Create a new ticket" button */
  raiseTicketUrl?: string;
  /** Custom fetch function (defaults to window.fetch) */
  fetchFn?: typeof window.fetch;
  /** Custom CSS class */
  className?: string;
  /** Custom inline styles */
  style?: React.CSSProperties;
}

export const TicketListPage: React.FC<TicketListPageProps> = ({
  fetchUrl = '/manage/ticket.cgi?action=list-jqgrid',
  raiseTicketUrl = '/manage/ticket.cgi?action=raise_form',
  fetchFn = window.fetch.bind(window),
  className = '',
  style,
}) => {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [inputValue, setInputValue] = useState('');
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortKey, setSortKey] = useState('status');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [hasLoaded, setHasLoaded] = useState(false);

  const abortRef = useRef<AbortController | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fetchFnRef = useRef(fetchFn);

  useEffect(() => {
    fetchFnRef.current = fetchFn;
  }, [fetchFn]);

  /* eslint-disable react-hooks/set-state-in-effect -- data fetching effect legitimately sets loading state */
  useEffect(() => {
    if (abortRef.current) {
      abortRef.current.abort();
    }

    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);

    const params = new URLSearchParams({
      search,
      page: String(currentPage),
      rows: String(pageSize),
      sidx: sortKey,
      sord: sortDirection,
    });

    fetchFnRef
      .current(`${fetchUrl}&${params.toString()}`, { signal: controller.signal })
      .then((res) => res.json())
      .then((data: JqGridResponse) => {
        setTickets(data.rows || []);
        setTotalRecords(data.records || 0);
        setLoading(false);
        setHasLoaded(true);
      })
      .catch((err) => {
        if (err.name !== 'AbortError') {
          setLoading(false);
          setHasLoaded(true);
        }
      });

    return () => {
      controller.abort();
    };
  }, [fetchUrl, search, currentPage, pageSize, sortKey, sortDirection]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      if (value.length >= 2 || value.length === 0) {
        setSearch(value);
        setCurrentPage(1);
      }
    }, 500);
  }, []);

  const handleClearSearch = useCallback(() => {
    setInputValue('');
    setSearch('');
    setCurrentPage(1);
    clearTimeout(debounceRef.current!);
  }, []);

  const handleSort = useCallback((key: string, direction: 'asc' | 'desc') => {
    setSortKey(key);
    setSortDirection(direction);
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  }, []);

  const handleRowClick = useCallback((row: Ticket) => {
    window.location.href = `/manage/ticket.cgi/${row.id}`;
  }, []);

  const columns: Column<Ticket>[] = [
    {
      key: 'id',
      label: 'Ticket number',
      width: '120px',
      align: 'left',
      render: (_value, row) => (
        <span className="hi-ui-ticket-list-page__id-cell">
          <Text variant="inline">{row.id}</Text>
          <Text variant="caption">
            ({row.update_count} update{row.update_count !== 1 ? 's' : ''})
          </Text>
        </span>
      ),
    },
    {
      key: 'title',
      label: 'Summary',
      sortable: true,
      render: (_value, row) => (
        <span className="hi-ui-ticket-list-page__summary-cell">
          <Text variant="inline" bold>
            {row.service}
          </Text>
          <Text variant="muted">{row.title}</Text>
        </span>
      ),
    },
    {
      key: 'updated',
      label: 'Date',
      sortable: true,
      width: '100px',
      align: 'center',
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      width: 'auto',
      align: 'left',
      render: (value) => {
        const statusInfo = STATUS_DISPLAY[String(value)] || {
          text: String(value),
          color: 'var(--hi-color-neutral, #6b7280)',
          badge: 'neutral' as BadgeVariant,
        };
        return (
          <>
            <Flex align="center" gap="sm" className="hi-ui-ticket-list-page__status-desktop">
              <span
                className="hi-ui-ticket-list-page__status-dot"
                style={{ backgroundColor: statusInfo.color }}
                aria-hidden="true"
              />
              <Text variant="body" inline>
                {statusInfo.text}
              </Text>
            </Flex>
            <span className="hi-ui-ticket-list-page__status-mobile">
              <Badge variant={statusInfo.badge} pill>
                {statusInfo.text}
              </Badge>
            </span>
          </>
        );
      },
    },
  ];

  const pagination: PaginationConfig = {
    currentPage,
    pageSize,
    totalItems: totalRecords,
    pageSizeOptions: [10, 25, 50],
  };

  const isEmpty = hasLoaded && !loading && tickets.length === 0;
  const classes = ['hi-ui-ticket-list-page', className].filter(Boolean).join(' ');

  return (
    <div className={classes} style={style}>
      <Text variant="heading-1">Contact Customer Services</Text>
      {!(isEmpty && !search) && (
        <Text variant="body">Your open and closed tickets are displayed below:</Text>
      )}

      {!(isEmpty && !search) && (
        <Flex align="center" justify="between" className="hi-ui-ticket-list-page__toolbar">
          <div
            className={`hi-ui-ticket-list-page__search${inputValue ? ' hi-ui-ticket-list-page__search--has-value' : ''}`}
          >
            <Input
              placeholder="Filter by ticket number, summary or date"
              value={inputValue}
              onChange={handleSearchChange}
              disabled={!hasLoaded && loading}
              aria-label="Search tickets"
              fullWidth
            />
            {inputValue && (
              <Button
                variant="tertiary"
                size="small"
                className="hi-ui-ticket-list-page__clear-search"
                onClick={handleClearSearch}
                aria-label="Clear search"
              >
                <Icon name="cross" size={14} />
              </Button>
            )}
          </div>
          <Button as="a" href={raiseTicketUrl}>
            Create new ticket
          </Button>
        </Flex>
      )}

      {isEmpty ? (
        <EmptyState
          title={
            search
              ? 'No tickets were found matching that search.'
              : 'No support tickets raised yet.'
          }
          action={
            !search ? (
              <Button as="a" href={raiseTicketUrl}>
                Create new ticket
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="hi-ui-ticket-list-page__table-wrapper">
          {loading && !hasLoaded ? (
            <Flex align="center" justify="center" className="hi-ui-ticket-list-page__loading">
              <Spinner size="large" aria-label="Loading tickets..." />
            </Flex>
          ) : (
            <Table<Ticket>
              columns={columns}
              data={tickets}
              loading={loading}
              loadingMessage="Loading tickets..."
              emptyMessage="No tickets found"
              sortKey={sortKey}
              sortDirection={sortDirection}
              onSort={handleSort}
              onRowClick={handleRowClick}
              pagination={pagination}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              getRowKey={(row) => String(row.id)}
              ariaLabel="Support tickets"
            />
          )}
        </div>
      )}
    </div>
  );
};
